import React, { useState } from 'react';
import Moment from 'moment';
import Label from '../../components/Label';
import SubmitButton from '../../components/SubmitButton';
import { useHistory } from 'react-router-dom';
import { toMoney } from '../../utilities/MoneyConversion';

interface OrdersProps {
  orders?: any[];
}

export default function OrderComponent({ orders }: OrdersProps): JSX.Element {
  const history = useHistory();
  const [showMore, setShowMore] = useState(false);

  return (
    <>
      <section className="all-orders-info">
        {orders?.slice(0, showMore ? orders.length : 3).map((order) => {
          const orderId = order.id;
          const orderBoxes = order.orderBoxes;
          const isRemovalOrder = order.orderType === 'removal';
          const isQuoteRequest = (order as any).itemType === 'quote';
          
          // Create unique key by prefixing with type to avoid duplicate keys
          const itemType = isQuoteRequest ? 'quote' : (isRemovalOrder ? 'removal' : 'order');
          const uniqueKey = `${itemType}-${orderId}`;

          return (
            <div key={uniqueKey} className="each-order">
              <div className="borders">
                <div className="top-border">
                  {isQuoteRequest
                    ? `Custom Quote Request - ${(order as any).tierName}`
                    : isRemovalOrder
                    ? 'Removal Order'
                    : order?.suitcaseOrder
                      ? 'Suitcase Order'
                      : order?.storageRequired
                        ? 'Storage Order'
                        : 'Shipping Order'}
                </div>
                <div className="second-border">
                  <p className="date">
                    {isQuoteRequest ? 'Quote Requested' : 'Order Created'} At:{' '}
                    {Moment(order?.createdAt).format('dddd, MMM Do YYYY')}
                  </p>
                </div>
              </div>
              
              {/* Show status badge for quote requests */}
              {isQuoteRequest && (
                <div className="quote-status-badge" style={{ 
                  padding: '8px 16px', 
                  backgroundColor: (order as any).status === 'quoted' ? '#4CAF50' : '#FFA726',
                  color: 'white',
                  borderRadius: '4px',
                  margin: '10px 0',
                  textAlign: 'center'
                }}>
                  Status: {(order as any).status.toUpperCase()}
                  {(order as any).quotedPrice && ` - Quoted Price: ${toMoney((order as any).quotedPrice)}`}
                </div>
              )}
              <div className="content">
                <div className="id-and-city">
                  <p className="id">{order.id}</p>
                  <p className="city">
                    {isRemovalOrder ? order.collectionCity : order.city}
                  </p>
                </div>
                <div className="inner-line" />
                {isRemovalOrder ? (
                  <div className="order-details">
                    <Label htmlFor={`removal-details-${orderId}`}>
                      Removal Details
                    </Label>
                    <div className="details">
                      <Label htmlFor={`removal-tier-${orderId}`}>
                        Tier
                      </Label>
                      <p className="td" id={`removal-tier-${orderId}`}>
                        {order.tierName}
                      </p>
                      <Label htmlFor={`removal-moveDate-${orderId}`}>
                        Move Date
                      </Label>
                      <p className="td" id={`removal-moveDate-${orderId}`}>
                        {order.moveDate && Moment(order.moveDate).format('dddd, MMM Do YYYY')}
                      </p>
                    </div>
                  </div>
                ) : (
                  !order?.suitcaseOrder && (
                    <div className="order-details">
                      <Label htmlFor={`box-details-${orderId}`}>
                        Product Details
                      </Label>
                      <div className="details">
                        <Label htmlFor={`box-product-${orderId}`}>
                          Ordered Products Include
                        </Label>
                        <p className="td" id={`box-eachItem-${orderId}`}>
                          {orderBoxes?.map((orderBox: any) => {
                            return (
                              <span
                                key={orderBox.id}
                                >{`${orderBox?.box?.name}, `}</span>
                            );
                          })}
                        </p>
                      </div>
                    </div>
                  )
                )}
                <div className="cost-details">
                  <Label htmlFor={`cost-details-${orderId}`}>
                    Cost Details
                  </Label>
                  <div className="details">
                    <Label htmlFor={`cost-initialCost-${orderId}`}>
                      Initial Cost
                    </Label>
                    <p className="td" id={`cost-initalCost-${orderId}`}>
                      {toMoney(isRemovalOrder ? (order.totalInitialCost || 0) : (order.initialCost || 0))}
                    </p>
                    <Label htmlFor={`cost-monthlyCost-${orderId}`}>
                      Monthly Cost
                    </Label>
                    <p className="td" id={`cost-monthlyCost-${orderId}`}>
                      {toMoney(isRemovalOrder ? (order.monthlyStorageCost || 0) : (order.monthlyCost || 0))}
                    </p>
                  </div>
                </div>
                <div className="all-buttons">
                  <SubmitButton
                    onClick={() => history.push(
                      isQuoteRequest 
                        ? `/removals/quote/${order.id}` 
                        : isRemovalOrder 
                        ? `/removals/order/${order.id}` 
                        : `/order/${order.id}`
                    )}
                  >
                    Details
                  </SubmitButton>
                </div>
              </div>
              <div className="orderDates">
                <div className="line" />
                <p className="updatedAt">
                  Last Updated:{' '}
                  {Moment(order.updatedAt).format('HH:mm, dddd, MMM Do YYYY')}
                </p>
              </div>
            </div>
          );
        })}
      </section>
      <section className="showMoreBtn">
        {orders && orders?.length >= 3 && (
          <SubmitButton
            onClick={() => {
              if (showMore) {
                setShowMore(false);
              } else {
                setShowMore(true);
              }
            }}
          >
            {showMore ? (
              <>
                <span>Show Less</span>
                <i className="fas fa-chevron-up" />
              </>
            ) : (
              <>
                <span>Show More</span>
                <i className="fas fa-chevron-down" />
              </>
            )}
          </SubmitButton>
        )}
      </section>
    </>
  );
}
